import { useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import Button from '../ui/elements/button'
import LinkButton from '../ui/elements/link'
import LoadingDots from '../ui/shared/loading-dots'
import { v4 as uuid } from 'uuid'
import ImagePositionImport from '../image-position-import'
import { costToString } from './cost'
import { cost as costPropType } from './prop-types'
import ValidationMessage from '../ui/shared/validation-message'
import PaymentsModal from '../payments/payments-modal'
import { getStripe } from '../../utils/get-stripe'
import { useProducts } from '../../utils/payment-utils'
import Gcp from './gcp'
import bbox from '@turf/bbox'
import { buffer } from 'ol/extent'
import Combobox, { ComboboxWithCreate } from '../ui/elements/combobox'

export default function UploadFiles({
  crops,
  crop,
  files,
  status,
  farms,
  farmId,
  fieldName,
  fields,
  onFarmChange,
  onFieldNameChange,
  onCropChange,
  onUpload,
  onPermissionsUpdated,
  projectId,
  projectType,
  options,
  onOptionChange,
  gcpEnabled,
  onAddFiles,
  metaData,
  cost,
  validationMessage,
  onUpdateMetadata,
  units,
  mapboxToken,
  apiClient,
  stripeToken,
  currency,
  purchasesEnabled,
  uploadProgress,
  setProcessingOption,
}) {
  // Disable Beacon in this view, since it clashes with the upload button
  useEffect(() => {
    window.Beacon?.('destroy')
  }, [])

  const numberOfFiles = Object.keys(files).length
  const getNumberOfUploadedFiles = () =>
    Object.values(files).filter(file => file.uploadStatus === 'done').length
  const isUploading = status === 'uploading'
  const isDoneUploading = status === 'success'
  const hasUploadError = status === 'error'
  const missingGeoreference =
    metaData && metaData.features.some(f => !f.geometry)
  const hasMissingFiles = useMemo(
    () => Object.keys(files).some(id => !files[id].file),
    [files]
  )
  const inEditMode = !isUploading && !isDoneUploading && !projectId
  const isOverlappingProject = projectType === 'overlapping'
  const [showGcp, setShowGcp] = useState(false)
  const [gcpState, setGcpState] = useState()
  const [showBuyAddOn, setShowBuyAddOn] = useState(false)
  const [showImportPositions, setImportPositions] = useState(false)
  const [positionsFile, setPositionsFile] = useState(null)
  const positionImportFileInput = useRef()
  const validated = !validationMessage
  const gcpAddOnProduct = useProducts(apiClient).filter(
    ({ id }) => id === 'gcp'
  )

  const success = projectType => {
    if (projectType === 'individual') {
      return {
        message: 'Images have been successfully uploaded',
        route: `/projects/${projectId}`,
        linkText: 'Next',
      }
    } else if (projectType === 'overlapping' || projectType === 'geotiff') {
      return {
        message:
          "Images have been successfully uploaded and processing has started. Once ready, we'll send you an email",
        route: `/farms`,
        linkText: 'Return home',
      }
    } else {
      throw new Error(`Unknown project type "${projectType}.`)
    }
  }

  const selectedFarmValue = useMemo(() => {
    const farm = farms.find(f => f.id === farmId)
    return farm && { label: farm.name, value: farmId }
  }, [farms, farmId])
  const farmOptions = useMemo(
    () => farms.map(f => ({ label: f.name, value: f.id })),
    [farms]
  )
  const currentFarmFields = useMemo(
    () =>
      fields
        .filter(f => f.farm_id === farmId)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [fields, farmId]
  )

  return (
    <>
      <div className="tw-pt-6 tw--mx-6 tw-px-6 tw-custom-border-top tw-z-30">
        <div className="tw-flex tw-justify-between">
          <h2 className="tw-text-bold tw-m-0 tw-text-lg tw-font-bold tw-mb-6">{`${
            inEditMode
              ? 'Upload Images'
              : isUploading
              ? 'Uploading images...'
              : 'Upload complete'
          }`}</h2>
          {!inEditMode && (
            <span className="tw-custom-uppercase tw-text-sm tw-text-system-grey-400">
              {uploadProgress != null
                ? `${uploadProgress.toFixed(0)}%`
                : `${getNumberOfUploadedFiles()}/${numberOfFiles} uploaded`}
            </span>
          )}
        </div>
        <div className="tw-flex tw-flex-col tw-gap-2">
          <div>
            <label
              className="tw-text-sm tw-text-system-grey-400 tw-mb-1 tw-custom-uppercase"
              htmlFor="field-name-upload">
              Farm
            </label>
            <Combobox
              value={selectedFarmValue}
              options={farmOptions}
              onChange={v => onFarmChange(v.value)}
              disabled={!inEditMode}
            />
          </div>
          <div>
            <label
              className="tw-text-sm tw-text-system-grey-400 tw-mb-1 tw-custom-uppercase"
              htmlFor="field-name-upload">
              Field
            </label>
            <ComboboxWithCreate
              value={fieldName ? { label: fieldName, value: fieldName } : null}
              options={currentFarmFields}
              placeholder="Select a field"
              createNew={{
                modalTitle: 'Create New Field',
                inputLabel: 'Field Name',
                inputPlaceholder: '',
                onCreate: label => ({ value: -1, label }),
              }}
              disabled={!inEditMode}
              onChange={v => onFieldNameChange(v.label)}
            />
          </div>
          {crops && (
            <div>
              <label
                className="tw-text-sm tw-text-system-grey-400 tw-mb-1 tw-custom-uppercase"
                htmlFor="field-name-upload">
                Crop
              </label>
              <ComboboxWithCreate
                value={{ label: crop, value: crop }}
                options={crops}
                placeholder="Select a crop"
                createNew={{
                  modalTitle: 'New Crop',
                  inputLabel: 'Crop Name',
                  inputPlaceholder: '',
                  onCreate: label => ({ value: -1, label }),
                }}
                disabled={!inEditMode}
                onChange={v => onCropChange(v.label)}
              />
            </div>
          )}
        </div>
        {options.length > 0 && (
          <div className="tw-uppercase tw-my-3">
            {options.map(({ key, Component }) => {
              return (
                <div key={key} className="tw-mb-2">
                  <Component
                    options={options}
                    disabled={!inEditMode}
                    onChange={active => onOptionChange(key, active)}
                  />
                </div>
              )
            })}
          </div>
        )}

        {isOverlappingProject && (gcpEnabled || purchasesEnabled) && (
          <div className="tw-my-3 tw-flex">
            {!gcpState ? (
              <Button
                icon="plus"
                size="medium"
                className="tw-flex-1"
                disabled={!inEditMode || hasMissingFiles}
                title={
                  hasMissingFiles
                    ? 'Add missing images before adding GCPs'
                    : null
                }
                onClick={() =>
                  gcpEnabled ? setShowGcp(uuid()) : setShowBuyAddOn(true)
                }>
                Add Ground Control Points
              </Button>
            ) : (
              <div className="tw-w-full tw-flex tw-flex-row tw-justify-between tw-items-center">
                <span>
                  {
                    gcpState.gcps.filter(p => isValidGcp(p, gcpState.markers))
                      .length
                  }{' '}
                  GCPs registered
                </span>
                <Button
                  size="normal"
                  action="default"
                  disabled={!inEditMode}
                  onClick={() => setShowGcp(uuid())}>
                  Edit
                </Button>
              </div>
            )}
          </div>
        )}
        {missingGeoreference && (
          <div className="tw-my-3 tw-flex">
            <Button
              icon="file-import"
              size="medium"
              className="tw-flex-1"
              disabled={!inEditMode}
              onClick={() => {
                positionImportFileInput.current.value = null
                positionImportFileInput.current.click()
              }}>
              Import Image Positions
            </Button>
          </div>
        )}

        {cost && inEditMode && (
          <ValidationMessage>{costToString(cost, units)}</ValidationMessage>
        )}
        {isDoneUploading ||
          (hasUploadError && (
            <ValidationMessage type={hasUploadError ? 'error' : 'default'}>
              {(isDoneUploading && <>{success(projectType).message}</>) ||
                (hasUploadError &&
                  'Something went wrong when uploading images, please try again')}
            </ValidationMessage>
          ))}

        <div className="tw-mt-3">
          {isDoneUploading && projectId ? (
            <LinkButton
              size="large"
              action="success"
              href={success(projectType).route}>
              {success(projectType).linkText}
            </LinkButton>
          ) : (
            <Button
              disabled={isUploading || isDoneUploading || !validated}
              size="large"
              action="primary"
              onClick={onUpload}>
              {isUploading ? (
                <LoadingDots />
              ) : (
                `Upload ${numberOfFiles} ${
                  numberOfFiles === 1 ? 'image' : 'images'
                }`
              )}
            </Button>
          )}
        </div>

        {gcpEnabled && showGcp && (
          <Gcp
            key={showGcp}
            bounds={buffer(bbox(metaData), 0.05)}
            images={metaData}
            files={files}
            gcpState={gcpState}
            mapboxToken={mapboxToken}
            onRegister={onCloseGcp}
            onCancel={() => setShowGcp(false)}
          />
        )}
        <input
          type="file"
          accept=".csv"
          ref={positionImportFileInput}
          style={{ display: 'none' }}
          onChange={() => {
            const file = positionImportFileInput.current.files[0]
            if (file) {
              setPositionsFile(file)
              setImportPositions(true)
            }
          }}
        />
        {positionsFile && showImportPositions && (
          <ImagePositionImport
            imageFilenames={Object.values(files).map(f => f.file.name)}
            file={positionsFile}
            show={showImportPositions}
            skipMinimumNumberMapped={true}
            onClose={onCloseImportPositions}
          />
        )}
      </div>
      <PaymentsModal
        show={showBuyAddOn}
        title="Add-On: Ground Control Points"
        currency={currency}
        instructions={
          <>
            <p className="tw-custom-reset-heading tw-mb-6">
              Achieve centimeter-level accuracy with your maps using Ground
              Control Points (GCP).
            </p>
            <p className="tw-custom-reset-heading tw-mb-6">
              GCP is an add-on that allows registering Ground Control Points in
              the images and use them to more accurately georeference the maps.
            </p>
          </>
        }
        onDismiss={() => {
          setShowBuyAddOn(false)
          onPermissionsUpdated()
          setShowGcp(uuid())
        }}
        products={gcpAddOnProduct}
        defaultProductId={gcpAddOnProduct[0]?.id}
        createSubscription={subscriptionOptions =>
          apiClient.subscriptions.create({ ...subscriptionOptions })
        }
        getPurchaseUrl={apiClient.user.getPurchaseUrl.bind(apiClient.user)}
        getUserPaymentMethod={apiClient.user.defaultPaymentMethod.bind(
          apiClient.user
        )}
        getStripe={() => getStripe(stripeToken)}
      />
    </>
  )

  function onCloseGcp(state) {
    setShowGcp(false)
    setGcpState(state)
    const [pointsCsv, markersCsv] = createGcpFiles(state)

    if (pointsCsv.length > 0 && markersCsv.length > 0) {
      onAddFiles([
        new File([pointsCsv], 'gcp_markers.csv', { type: 'text/csv' }),
        new File([markersCsv], 'gcp_projections.csv', { type: 'text/csv' }),
      ])

      setProcessingOption('gcpCrs', state.crs)
    }
  }

  function onCloseImportPositions(save, positions) {
    setImportPositions(false)
    if (save) {
      const existingFeatures = {}
      for (const position of positions) {
        const existingFeature = metaData.features.find(
          f => f.properties.filename === position.image
        )
        if (existingFeature) {
          existingFeatures[position.image] = existingFeature
        } else {
          console.warn(
            `Could not find a matching feature for image ${position.image}`
          )
        }
      }

      const positionsCsv = positions
        .filter(p => !!existingFeatures[p.image])
        .map(p =>
          [existingFeatures[p.image].id, p.lng, p.lat, p.z]
            .filter(x => x)
            .join(',')
        )
        .join('\n')
      onAddFiles([
        new File([positionsCsv], 'camera_positions.csv', { type: 'text/csv' }),
      ])
      const newFeatures = positions.map(p => {
        const existingFeature = existingFeatures[p.image]
        return (
          existingFeature && {
            ...existingFeature,
            geometry: {
              type: 'Point',
              coordinates: [p.lng, p.lat, p.z].map(Number),
            },
            properties: {
              ...existingFeature.properties,
              latitude: Number(p.lat),
              longitude: Number(p.lng),
              absolute_altitude: Number(p.z),
            },
          }
        )
      })
      onUpdateMetadata({
        geojson: { type: 'FeatureCollection', features: newFeatures },
      })
    }
  }
}

UploadFiles.propTypes = {
  crops: PropTypes.array,
  files: PropTypes.object,
  projectId: PropTypes.number,
  projectType: PropTypes.string,
  crop: PropTypes.string,
  status: PropTypes.string,
  fieldName: PropTypes.string,
  fields: PropTypes.array,
  onFieldNameChange: PropTypes.func,
  onCropChange: PropTypes.func,
  onUpload: PropTypes.func,
  farms: PropTypes.array,
  farmId: PropTypes.number,
  onFarmChange: PropTypes.func,
  options: PropTypes.array,
  onOptionChange: PropTypes.func,
  onPermissionsUpdated: PropTypes.func,
  gcpEnabled: PropTypes.bool,
  onAddFiles: PropTypes.func,
  metaData: PropTypes.object,
  cost: costPropType,
  validationMessage: PropTypes.node,
  onUpdateMetadata: PropTypes.func,
  units: PropTypes.string,
  mapboxToken: PropTypes.string,
  apiClient: PropTypes.shape({
    subscriptions: PropTypes.shape({ create: PropTypes.func }),
    user: PropTypes.shape({
      defaultPaymentMethod: PropTypes.func,
      getPurchaseUrl: PropTypes.func,
    }),
  }),
  stripeToken: PropTypes.string,
  currency: PropTypes.string,
  purchasesEnabled: PropTypes.bool,
  uploadProgress: PropTypes.number,
  setProcessingOption: PropTypes.func,
}

export function createGcpFiles({ gcps, markers }) {
  const validGcps = gcps.filter(p => isValidGcp(p, markers))
  const pointsCsv = validGcps
    .map(p => [p.id, ...p.coordinates].join(','))
    .join('\n')
  const markersCsv = markers
    .filter(m => validGcps.some(gcp => gcp.id === m.gcpId))
    .map(m =>
      [
        m.gcpId,
        m.imageFilename.substring(0, m.imageFilename.lastIndexOf('.')),
        ...m.coordinates,
      ].join(',')
    )
    .join('\n')

  return [pointsCsv, markersCsv]
}

function isValidGcp(gcp, markers) {
  return markers.filter(marker => marker.gcpId === gcp.id).length >= 3
}
